import { createError, RelodeApiError } from "@relode/core";
import {
    convertMarketResearchCompensationResponse,
    convertMarketResearchDiversityResponse,
    convertMarketResearchShareResponse,
    convertMarketResearchSummaryResponse,
    convertMarketResearchSupplyDemandResponse,
} from "@relode/core/backend/marketResearch";

import { converters } from "../../../shared/backend";
import { jobs, marketResearch } from "../../../shared/backend/controllers";
import { SetAddTeamMemberSubmitStatus } from "../details/job/jobDetails/addTeamMemberDialogProps";
import { SetChangeJobStatusStatus } from "../details/job/jobDetails/changeJobStatusDialogProps";
import { creators, JobActionTypes } from "./jobActionTypes";
import { JobState } from "./jobState";

export const useJobActions = (
    state: JobState,
    dispatch: React.Dispatch<JobActionTypes>
) => {
    const loadJob = async (jobId: number) => {
        try {
            dispatch(creators.loadJob());
            const detailsResponse = await jobs.jobService.getJobDetails(jobId);
            const details =
                converters.convertJobDetailsResponse(detailsResponse);
            dispatch(creators.loadJobSuccess(jobId, details));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadJobFailure(err));
                    return;
                }
            }
            throw createError(err, "jobs.jobActions.loadJob");
        }
    };

    const reloadJob = async () => {
        await loadJob(state.data.currentJobId);
    };

    const loadTeamMemberSuggestions = async (
        jobId: number,
        searchText: string
    ) => {
        try {
            dispatch(creators.loadTeamMemberSuggestions());
            const response = await jobs.jobService.getTeamMemberSuggestions(
                jobId,
                searchText
            );
            const suggestions =
                converters.convertTeamMemberSuggestionsResponse(response);
            dispatch(creators.loadTeamMemberSuggestionsSuccess(suggestions));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadTeamMemberSuggestionsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.jobs.JobActions.leanTeamMemberSuggestions"
            );
        }
    };

    const loadShareLink = async (jobId: number) => {
        try {
            dispatch(creators.loadJobShareLink());
            const response = await jobs.jobService.getShareLink(jobId);
            const jobShareInfo = converters.convertShareJobResponse(response);

            dispatch(creators.loadJobShareLinkSuccess(jobShareInfo, jobId));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadJobShareLinkFailure(err));
                    return;
                }
            }
            throw createError(err, "jobs.jobActions.loadShareLink");
        }
    };

    const addNewTeamMember = async (
        jobId: number,
        userId: number,
        clientJobUserTypeId: number,
        setAddTeamMemberSubmitStatus: SetAddTeamMemberSubmitStatus
    ) => {
        dispatch(creators.managerTeamMember());
        try {
            await jobs.jobService.addTeamMember(jobId, {
                userId: userId,
                clientJobUserTypeId: clientJobUserTypeId,
            });
            dispatch(
                creators.manageTeamMemberSuccess(
                    "team member added successfully"
                )
            );

            setAddTeamMemberSubmitStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageTeamMemberFailure(err));
                    setAddTeamMemberSubmitStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "jobs.jobActions.addNewTeamMember");
        }
    };

    const updateTeamMember = async (
        jobId: number,
        userId: number,
        clientJobUserTypeId: number,
        setAddTeamMemberSubmitStatus: SetAddTeamMemberSubmitStatus
    ) => {
        dispatch(creators.managerTeamMember());
        try {
            await jobs.jobService.updateTeamMember(jobId, {
                userId: userId,
                clientJobUserTypeId: clientJobUserTypeId,
            });
            dispatch(
                creators.manageTeamMemberSuccess(
                    "team member updated successfully"
                )
            );

            setAddTeamMemberSubmitStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageTeamMemberFailure(err));
                    setAddTeamMemberSubmitStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "jobs.jobActions.updateTeamMember");
        }
    };

    const removeTeamMember = async (jobId: number, userId: number) => {
        dispatch(creators.managerTeamMember());
        try {
            await jobs.jobService.deleteTeamMember(jobId, userId);
            dispatch(
                creators.manageTeamMemberSuccess(
                    "team member removed successfully"
                )
            );

            return true;
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageTeamMemberFailure(err));
                    return false;
                }
            }
            throw createError(err, "jobs.jobActions.removeTeamMember");
        }
    };

    const closeJob = async (
        jobId: number,
        comment: string,
        jobStatusReasonTypeId: number,
        concurrencyToken: string,
        setChangeJobStatusStatus: SetChangeJobStatusStatus
    ) => {
        dispatch(creators.updateJobStatus());
        try {
            await jobs.jobService.closeJob(jobId, {
                comment: comment,
                jobStatusReasonTypeId: jobStatusReasonTypeId,
                jobConcurrencyToken: concurrencyToken,
            });

            const detailsResponse = await jobs.jobService.getJobDetails(jobId);
            const details =
                converters.convertJobDetailsResponse(detailsResponse);

            dispatch(creators.updateJobStatusSuccess(details, "job closed"));
            setChangeJobStatusStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateJobStatusFailure(err));
                    setChangeJobStatusStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.jobActions.closeJob");
        }
    };

    const openJob = async (
        jobId: number,
        comment: string,
        concurrencyToken: string,
        setChangeJobStatusStatus: SetChangeJobStatusStatus
    ) => {
        dispatch(creators.updateJobStatus());
        try {
            await jobs.jobService.openJob(jobId, {
                comment: comment,
                jobConcurrencyToken: concurrencyToken,
            });

            const detailsResponse = await jobs.jobService.getJobDetails(jobId);
            const details =
                converters.convertJobDetailsResponse(detailsResponse);

            dispatch(creators.updateJobStatusSuccess(details, "job opened"));
            setChangeJobStatusStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateJobStatusFailure(err));
                    setChangeJobStatusStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.jobActions.openJob");
        }
    };

    const pauseJob = async (
        jobId: number,
        comment: string,
        pauseJobReasonId: number,
        concurrencyToken: string,
        setChangeJobStatusStatus: SetChangeJobStatusStatus
    ) => {
        dispatch(creators.updateJobStatus());
        try {
            await jobs.jobService.pauseJob(jobId, {
                comment: comment,
                jobStatusReasonTypeId: pauseJobReasonId,
                jobConcurrencyToken: concurrencyToken,
            });

            const detailsResponse = await jobs.jobService.getJobDetails(jobId);
            const details =
                converters.convertJobDetailsResponse(detailsResponse);

            dispatch(creators.updateJobStatusSuccess(details, "job paused"));
            setChangeJobStatusStatus("success", undefined);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.updateJobStatusFailure(err));
                    setChangeJobStatusStatus("failure", err);
                    return;
                }
            }
            throw createError(err, "client.jobActions.pauseJob");
        }
    };

    const setSelectedTab = (tab: string) => {
        dispatch(creators.setSelectedTab(tab));
    };

    const clearError = () => {
        dispatch(creators.clearError());
    };

    const loadMarketResearchCompensation = async (jobId: number) => {
        dispatch(creators.loadMarketResearchCompensation());
        try {
            const response =
                await marketResearch.marketResearchService.getJobCompensation(
                    jobId
                );
            const data = convertMarketResearchCompensationResponse(response);
            dispatch(creators.loadMarketResearchCompensationSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchCompensationFailure(err));
                return;
            }
            throw createError(
                err,
                "client.jobActions.loadMarketResearchCompensation"
            );
        }
    };

    const loadMarketResearchSupplyDemand = async (jobId: number) => {
        dispatch(creators.loadMarketResearchSupplyDemand());
        try {
            const response =
                await marketResearch.marketResearchService.getJobSupplyDemand(
                    jobId
                );
            const data = convertMarketResearchSupplyDemandResponse(response);
            dispatch(creators.loadMarketResearchSupplyDemandSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSupplyDemandFailure(err));
                return;
            }
            throw createError(
                err,
                "admin.jobActions.loadMarketResearchSupplyDemand"
            );
        }
    };

    const loadMarketResearchDiversity = async (jobId: number) => {
        dispatch(creators.loadMarketResearchDiversity());
        try {
            const response =
                await marketResearch.marketResearchService.getJobDiversity(
                    jobId
                );

            const data = convertMarketResearchDiversityResponse(response);
            dispatch(creators.loadMarketResearchDiversitySuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchDiversityFailure(err));
                return;
            }
            throw createError(
                err,
                "client.jobActions.loadMarketResearchDiversity"
            );
        }
    };

    const loadMarketResearchShare = async (jobId: number) => {
        dispatch(creators.loadMarketResearchShare());
        try {
            const response =
                await marketResearch.marketResearchService.getJobShare(jobId);

            const data = convertMarketResearchShareResponse(response);

            dispatch(creators.loadMarketResearchShareSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchShareFailure(err));
                return;
            }
            throw createError(err, "client.jobActions.loadMarketResearchShare");
        }
    };

    const loadMarketResearchSummary = async (jobId: number) => {
        dispatch(creators.loadMarketResearchSummary());
        try {
            const response =
                await marketResearch.marketResearchService.getJobSummary(jobId);

            const summary = convertMarketResearchSummaryResponse(response);
            
            dispatch(creators.loadMarketResearchSummarySuccess(summary));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                // JLC: We don't want to crash the app when market research fails for any API error
                dispatch(creators.loadMarketResearchSummaryFailure(err));
                return;
            }
            throw createError(
                err,
                "client.jobActions.loadMarketResearchSummary"
            );
        }
    };

    const loadJobCandidatesFunnel = async (jobId: number) => {
        try {
            dispatch(creators.loadJobCandidatesFunnel());

            const jobCandidatesFunnelResponse =
                await jobs.jobService.loadJobCandidatesFunnel(jobId);
            const jobCandidatesFunnel =
                converters.convertJobCandidatesFunnelResponse(
                    jobCandidatesFunnelResponse
                );

            dispatch(
                creators.loadJobCandidatesFunnelSuccess(jobCandidatesFunnel)
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;

                dispatch(creators.loadJobCandidatesFunnelFailure(err));
                return;
            }
            throw createError(
                err,
                "jobs.job.jobActions.loadJobCandidatesFunnel"
            );
        }
    };

    const loadJobEngagementFunnel = async (jobId: number) => {
        try {
            dispatch(creators.loadJobEngagementFunnel());

            const jobEngagementFunnelResponse =
                await jobs.jobService.loadJobEngagementFunnel(jobId);
            const jobEngagementFunnel =
                converters.convertJobEngagementFunnelResponse(
                    jobEngagementFunnelResponse
                );

            dispatch(
                creators.loadJobEngagementFunnelSuccess(jobEngagementFunnel)
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;

                dispatch(creators.loadJobEngagementFunnelFailure(err));
                return;
            }
            throw createError(
                err,
                "jobs.job.jobActions.loadJobEngagementFunnel"
            );
        }
    };

    const loadJobStats = async (jobId: number) => {
        try {
            dispatch(creators.loadJobStats());

            const jobStatsResponse = await jobs.jobService.loadJobStats(jobId);
            const jobStats =
                converters.convertJobStatsResponse(jobStatsResponse);

            dispatch(creators.loadJobStatsSuccess(jobStats));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;

                dispatch(creators.loadJobStatsFailure(err));
                return;
            }
            throw createError(err, "jobs.job.jobActions.loadJobStats");
        }
    };

    return {
        loadJob,
        reloadJob,
        loadShareLink,
        loadTeamMemberSuggestions,

        addNewTeamMember,
        updateTeamMember,
        removeTeamMember,

        closeJob,
        openJob,
        pauseJob,
        clearError,
        setSelectedTab,

        loadJobCandidatesFunnel,
        loadJobEngagementFunnel,
        loadJobStats,

        loadMarketResearchShare,
        loadMarketResearchSummary,
        loadMarketResearchCompensation,
        loadMarketResearchDiversity,
        loadMarketResearchSupplyDemand,
    };
};

export type JobActions = ReturnType<typeof useJobActions>;
